import React, { ReactElement } from 'react';
import styled from 'styled-components';
import {
  RecordColumn,
  RecordRowPresentation,
} from '../../../common/model/ui/record-list.type';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import useRowNavigation from '../../../hooks/use-row-navigation';
import OvLabelledField from '../atoms/OvLabelledField';

interface OvRecordListCardProps<T> {
  record: T;
  columns: RecordColumn<T>[];
  detailsPath?: string;
  getNavigationState?: () => unknown;
  presentation?: RecordRowPresentation;
}

const OvRecordListCard = <T,>({
  record,
  columns,
  detailsPath,
  getNavigationState,
  presentation = {},
}: OvRecordListCardProps<T>): ReactElement => {
  const navigate = useRowNavigation(detailsPath ?? '', getNavigationState);
  const renderValue = (column: RecordColumn<T>) =>
    (column.renderCardValue ?? column.renderCell)(record, detailsPath);
  const byRole = (role: RecordColumn<T>['cardRole']) =>
    columns.filter((column) => (column.cardRole ?? 'field') === role);

  return (
    <Card
      $isClickable={!!detailsPath}
      $isFlagged={!!presentation.isFlagged}
      data-muted={presentation.isMuted || undefined}
      onClick={detailsPath ? navigate : undefined}
    >
      <Top>
        <Heading>
          {byRole('heading').map((column) => (
            <div key={column.id}>{renderValue(column)}</div>
          ))}
        </Heading>
        {byRole('footer').map((column) => (
          <div key={column.id}>{renderValue(column)}</div>
        ))}
      </Top>
      <Fields>
        {byRole('field').map((column) => (
          <OvLabelledField
            key={column.id}
            label={column.cardLabel ?? column.header}
          >
            {renderValue(column)}
          </OvLabelledField>
        ))}
      </Fields>
    </Card>
  );
};

export default OvRecordListCard;

const Card = styled.li<{ $isClickable: boolean; $isFlagged: boolean }>`
  padding: ${Variables.spacing.MEDIUM} ${Variables.spacing.SMALL};
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  list-style: none;
  ${({ $isClickable }) => $isClickable && 'cursor: pointer;'}
  ${({ $isFlagged }) =>
    $isFlagged && `box-shadow: inset 3px 0 0 ${Colours.OV_RED};`}

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &[data-muted] {
    color: ${Colours.OV_CHARCOAL};
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Variables.spacing.LARGE};
`;

const Heading = styled.div`
  min-width: 0;
`;

const Fields = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
  gap: ${Variables.spacing.SMALL} ${Variables.spacing.LARGE};
  margin: ${Variables.spacing.SMALL} 0 0;

  &:empty {
    display: none;
  }
`;
